import React from "react";
import { X } from "lucide-react";
import type { TokenResponse, WorkspaceOption, WorkspaceSelectionResponse } from "../../../domain/types/auth";
import { STAFF_ROLE_LABELS } from "../../../domain/types/staff";
import { useAuthStore } from "../../stores/auth-store";

interface Props {
  selection: WorkspaceSelectionResponse;
  onClose: () => void;
  /** 선택한 워크스페이스로 로그인을 마친 뒤 */
  onCompleted: (token: TokenResponse, workspace: WorkspaceOption) => void;
}

/**
 * 한 계정이 여러 회사에 속해 있을 때 들어갈 회사를 고른다.
 *
 * 선택 토큰은 5분만 유효하다. 만료되면 처음부터 다시 로그인해야 한다.
 */
export const WorkspacePickerDialog: React.FC<Props> = ({ selection, onClose, onCompleted }) => {
  const selectWorkspace = useAuthStore((state) => state.selectWorkspace);
  const [busyUserId, setBusyUserId] = React.useState<number | null>(null);
  const [expired, setExpired] = React.useState(false);

  const handleSelect = async (workspace: WorkspaceOption) => {
    setBusyUserId(workspace.userId);
    try {
      const token = await selectWorkspace(selection.selectionToken, workspace.userId);
      onCompleted(token, workspace);
    } catch (error) {
      console.error("워크스페이스 선택 실패:", error);
      setExpired(true);
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center bg-black/40">
      <div className="w-[420px] max-w-[calc(100vw-32px)] rounded-[12px] bg-white shadow-lg overflow-hidden text-left">
        <div className="flex items-center justify-between p-5 border-b border-[#eee]">
          <h3 className="text-[16px] font-bold text-[#222]">워크스페이스 선택</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#666]" aria-label="닫기">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {expired ? (
            <>
              <div className="rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">
                선택 시간이 지났거나 처리하지 못했습니다. 다시 로그인해 주세요.
              </div>
              <button
                onClick={onClose}
                className="mt-4 w-full h-[44px] text-[14px] rounded-[8px] bg-[#222] text-white hover:bg-[#333]"
              >
                다시 로그인하기
              </button>
            </>
          ) : (
            <>
              <p className="text-[13px] text-[#888] mb-3">이 계정은 여러 회사에 속해 있습니다. 들어갈 회사를 선택해 주세요.</p>
              <div className="flex flex-col gap-2">
                {selection.workspaces.map((workspace) => (
                  <button
                    key={workspace.userId}
                    disabled={busyUserId !== null}
                    onClick={() => handleSelect(workspace)}
                    className="flex items-center justify-between px-4 py-3 rounded-[8px] border border-[#e0e0e0] hover:border-[#4285F4] hover:bg-[#F5F8FF] disabled:opacity-60 transition-colors"
                  >
                    <span className="text-[14px] font-medium text-[#222]">{workspace.companyName}</span>
                    <span className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#E8EFFF] text-[#4285F4]">
                        {STAFF_ROLE_LABELS[workspace.role] ?? workspace.role}
                      </span>
                      {busyUserId === workspace.userId && <span className="text-[12px] text-[#888]">들어가는 중...</span>}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** 워크스페이스로 들어간 뒤 갈 곳. 회사 소유자·관리자는 MES 홈, 그 외는 첫 화면 */
export const workspaceHomePath = (workspace: WorkspaceOption) =>
  workspace.role === "COMPANY_ADMIN" || workspace.role === "COMPANY_MANAGER" ? `/company/${workspace.companyId}` : "/";
